import { observer } from "mobx-react-lite"

import { BodyCaption } from "@/domains/design/components/body-caption"
import { BodyXs } from "@/domains/design/components/body-xs"
import { RiskProgressBar } from "@/domains/design/components/risk-progress-bar"
import { ScaleCard400 } from "@/domains/design/components/scale-card-400"
import { displayPercentage } from "@/domains/display/display-percentage"
import { CoinsIcon } from "@/domains/icons/huge-icons/monotone/coins"
import { cn, dollars } from "@/domains/library"
import { DisplayTokens } from "@/domains/tokens/components/display-tokens"

import type { RevenueSourceCardViewProps } from "./types"

export const RevenueSourceCardView = observer(
  ({ revenueSource, totalUsed, onUpdateAllocation }: RevenueSourceCardViewProps) => {
    const isActive = revenueSource.allocation > 0
    const available = Math.max(0, 100 - totalUsed)

    const handleMax = () => {
      const maxPossible = Math.min(100, revenueSource.allocation + available)
      onUpdateAllocation(revenueSource.id, maxPossible)
    }

    const handleInputChange = (val: string) => {
      let num = parseInt(val, 10)
      if (Number.isNaN(num)) num = 0
      if (num > 100) num = 100
      if (num < 0) num = 0
      onUpdateAllocation(revenueSource.id, num)
    }

    return (
      <div
        className={`group relative flex flex-col rounded-2xl border p-4 transition-all duration-200 ${
          isActive
            ? "border-primary-400/50 bg-p400 shadow-[0_0_20px_rgba(59,130,246,0.1)]"
            : "border-p400-light bg-p400 hover:border-p300-light hover:bg-p400-light"
        }`}
      >
        {/* Hero Section */}
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-12 items-center justify-center">
              <DisplayTokens tokens={revenueSource.tokens} tokenSize={28} />
            </div>
            <div>
              <div className="text-sm font-bold text-t100">{revenueSource.displayName ?? "Locating source..."}</div>
              <div className="text-xs text-t300">{revenueSource.protocol}</div>
            </div>
          </div>
          {/* Hero Metric: My $ / Vote (Green Highlight) */}
          <div className="flex flex-col items-end">
            <div className="text-lg font-bold text-green-400">${revenueSource.perVote.toFixed(6)}</div>
            <div className="text-[10px] font-bold uppercase text-t300">My $ / Vote</div>
          </div>
        </div>

        {/* Body Section */}
        <ScaleCard400 className="mx-3 mb-4 mt-2 rounded-md bg-p400-light px-4 py-3">
          <div className="grid grid-cols-3 justify-center gap-x-4 gap-y-2 text-xs">
            <div className="">
              <BodyCaption className="mb-0.5 text-t300">Revenue</BodyCaption>
              <BodyXs As="div" className="text-t200">
                ${revenueSource.fees.toLocaleString("en-US", { maximumFractionDigits: 2 })}
              </BodyXs>
            </div>
            {/* Secondary Metric: APR (moved down from hero) */}
            <div className="">
              <BodyCaption className="mb-0.5 text-t300">Emissions</BodyCaption>
              <BodyXs As="div" className="text-t200">
                {revenueSource.emissions.toLocaleString("en-US", { maximumFractionDigits: 0 })}
              </BodyXs>
            </div>
            <div className="">
              <BodyCaption className="mb-0.5 text-t300">TVL</BodyCaption>
              <BodyXs As="div" className="text-t200">
                ${revenueSource.tvlUsd?.toLocaleString("en-US", { maximumFractionDigits: 0 })}
              </BodyXs>
            </div>
            <div className="">
              <BodyCaption className="mb-0.5 text-t300">APR</BodyCaption>
              <BodyXs As="div" className="text-t200">
                {revenueSource.apr.toLocaleString("en-US", { maximumFractionDigits: 2 })}%
              </BodyXs>
            </div>
            <div className="">
              <BodyCaption className="mb-0.5 text-t300">Vote %</BodyCaption>
              <BodyXs As="div" className="text-t200">
                {displayPercentage(revenueSource.votesPercent)}
              </BodyXs>
            </div>

            <div className="">
              <BodyCaption className="mb-0.5 text-t300">Votes</BodyCaption>
              <BodyXs As="div" className="text-t200">
                {revenueSource.votes.toLocaleString("en-US", { maximumFractionDigits: 0 })}
              </BodyXs>
            </div>
          </div>
        </ScaleCard400>

        {/* Incentives Section */}
        <div className="mb-3 mt-3">
          <div className="text-slate-500 mb-1.5 flex items-center gap-[3px] text-[10px] font-bold uppercase tracking-wide">
            <CoinsIcon className="h-3 w-3 text-t300" />
            <BodyCaption className="mb-0 text-[9px] text-t300">Incentives</BodyCaption>
          </div>
          <div className="flex flex-wrap gap-2">
            {revenueSource.incentives
              .filter((incentive) => incentive.hasReward)
              .map((incentive) => (
                <ScaleCard400
                  className="flex items-center gap-2 rounded-md border bg-p400-light py-1 pl-1.5 pr-2.5 text-xs"
                  key={incentive.token.address.toString()}
                >
                  <DisplayTokens tokens={[incentive.token]} tokenSize={16} />
                  <div className="flex flex-col leading-none">
                    <span className="mb-1 font-bold text-t200">{incentive.token.symbol}</span>
                    <BodyCaption As="div" className="text-[10px] text-t200">
                      {dollars(incentive.rewardAmountUsd, 2)} {incentive.token.symbol}
                    </BodyCaption>
                  </div>
                </ScaleCard400>
              ))}
          </div>
        </div>

        {/* Footer Section: Percentage Input with Max Button */}
        <div className="mt-auto flex justify-between gap-4 pt-2">
          <RiskProgressBar
            title="Dilution Risk"
            value={revenueSource.dilutionRisk}
            rank={revenueSource.dilutionRiskRank}
            color={revenueSource.dilutionRiskColor}
            className="w-1/3"
            maxWidth={60}
          />
          {/* <RiskProgressBar title="Efficiency" value={revenueSource.efficiency} className="w-1/3" /> */}

          <div
            className={`flex w-1/3 items-center rounded-lg border p-1 ${
              isActive ? "border-primary-400/50" : "border-p400-light"
            } bg-p500`}
          >
            <div className="mr-1 flex flex-grow items-center justify-end bg-transparent outline-none">
              <input
                type="number"
                min="0"
                max="100"
                value={revenueSource.allocation > 0 ? revenueSource.allocation : ""}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder="0"
                className="flex-grow bg-transparent py-0.5 pl-2 pr-[3px] text-right text-sm font-bold text-t100 outline-none [-moz-appearance:textfield] placeholder:text-t300 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              <span className="mr-2 text-xs font-bold text-t300">%</span>
            </div>
            <button
              onClick={handleMax}
              className="h-full min-w-[40px] rounded bg-p400 px-0.5 text-center transition-colors hover:bg-p200-light"
            >
              <BodyCaption
                As="div"
                className="flex h-full items-center justify-center uppercase transition-colors hover:text-t200"
                options={{
                  desktop: { size: { px: 8, tw: "text-[8px]" }, lineHeight: { px: 8, tw: "leading-[8px]" } },
                }}
              >
                Max
              </BodyCaption>
            </button>
          </div>
        </div>
      </div>
    )
  },
)
